import { client } from '@/portalApi/client.gen'
import { PortalApiError } from '@/util/apiClient/PortalApiError'
import { bearerAuthHeaders } from '@/util/apiClient/authHeaders'
import getEnv from '@/util/env'
import * as Sentry from '@sentry/vue'

/**
 * Serializes query parameters, lifting `queryables` to the top level.
 *
 * `queryables` holds dynamic `field__operator=value` filters, and the spec
 * declares it `style: form, explode: true` — its keys belong on the query string
 * directly (`?name__ilike=foo`), not nested (`?queryables[name__ilike]=foo`).
 *
 * It is flattened here rather than configured as a per-parameter object style
 * because the built-in serializer rejects an array nested inside an object param
 * outright ("Deeply-nested arrays/objects aren't supported"), and filtering by a
 * set of ids depends on arrays: the API turns a repeated key into `= ANY(...)`.
 * Once lifted, every value is an ordinary top-level parameter.
 */
const querySerializer = (query: Record<string, unknown>): string => {
  const { queryables, ...rest } = query
  const flattened = {
    ...rest,
    ...((queryables as Record<string, unknown>) ?? {}),
  }
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(flattened)) {
    if (value === undefined || value === null) continue
    const append = (v: unknown) =>
      search.append(key, v instanceof Date ? v.toISOString() : String(v))
    if (Array.isArray(value)) value.forEach(append)
    else append(value)
  }
  return search.toString()
}

client.setConfig({
  // Runtime value, so one build artifact serves every deployment. The spec's own
  // `servers` entry is only a build-time default.
  baseUrl: getEnv('VITE_API_BASE_PATH'),
  querySerializer,
})

/*
  The auth store is read per request rather than once at setup, so a token
  refresh takes effect immediately and this never runs before Pinia is installed.
*/
client.interceptors.request.use((request) => {
  Object.entries({ ...bearerAuthHeaders(), ...Sentry.getTraceData() }).forEach(
    ([name, value]) => {
      if (value) request.headers.set(name, value)
    },
  )
  return request
})

/*
  A network failure produces no response and so has no status; those pass through
  untouched rather than being mislabelled.
*/
client.interceptors.error.use((error, response) =>
  response ? new PortalApiError(response.status, error) : error,
)

/**
 * The generated REST client, derived from portalbackend's OpenAPI spec.
 *
 * Each member is named after its spec `operationId`. Arguments are grouped by
 * where they belong in the request — `{ path, query, body }` — and a call
 * resolves to the parsed response body, throwing {@link PortalApiError} on a
 * non-2xx status.
 *
 * Reach the API through this module: importing `@/portalApi/sdk.gen` directly
 * skips the configuration above.
 */
export * as portalApi from '@/portalApi/sdk.gen'

/*
  Re-exported for convenience. Import it from './PortalApiError' directly in
  anything that mocks this module, or the mock will hide it.
*/
export { PortalApiError } from '@/util/apiClient/PortalApiError'
